import React from "react";

import { MultiSelect } from "~/components/multi-select";
import { MultiSelectItem } from "~/components/multi-select/item";

import {
	type DashboardListVariable,
	type DashboardStaticListVariable,
	sortDashboardListValues,
} from "../../../../common/dashboard-spec";

import { IncludeEverythingSwitch } from "./_include-everything-switch";

type MultiChoiceVariable = Exclude<DashboardListVariable, { allowMultiple: false }>;

interface Props extends Pick<MultiChoiceVariable, "allowAllValue" | "sort" | "defaultValue"> {
	onChange: (newValue: MultiChoiceVariable["defaultValue"]) => void;
	onDropdownVisibleChange?: (visible: boolean) => void;
	options: DashboardStaticListVariable["values"];
}

export const ListMultiChoiceVariable = ({
	allowAllValue,
	defaultValue,
	onChange,
	onDropdownVisibleChange,
	options,
	sort,
}: Props): React.JSX.Element => {
	const [previousValues, setPreviousValues] = React.useState<Array<string>>([]);
	const isEverythingSelected = defaultValue?.type === "Everything";
	const values = isEverythingSelected ? previousValues : (defaultValue?.values ?? []);

	const sortedOptions = React.useMemo(
		() =>
			sortDashboardListValues(options, sort).map((option) => ({
				value: option.value.toString(),
				label: option.label.toString(),
			})),
		[options, sort],
	);

	return (
		<>
			<MultiSelect
				disabled={isEverythingSelected}
				onDropdownVisibleChange={onDropdownVisibleChange}
				onValuesChange={(next) => onChange({ type: "Values", values: next })}
				values={values}
			>
				{sortedOptions.map(({ value, label }) => (
					<MultiSelectItem key={value} value={value}>
						{label}
					</MultiSelectItem>
				))}
			</MultiSelect>

			{allowAllValue && (
				<IncludeEverythingSwitch
					checked={isEverythingSelected}
					onChange={(checked) => {
						if (checked) {
							setPreviousValues(values);
							onChange({ type: "Everything" });
						} else {
							onChange({ type: "Values", values: previousValues });
						}
					}}
				/>
			)}
		</>
	);
};

export type ListMultiChoiceVariableProps = Props;
